import React, { useMemo, useState } from "react";
import { Plus, Edit2, Trash2, Eye, EyeOff, Facebook, Clock } from "lucide-react";
import { DEPARTMENTS } from "./galleryConstants";
import { Badge, formatDate } from "./adminUi";

const selectCls = "px-3 py-2 border border-gray-200 rounded-sm text-sm bg-white focus:outline-none focus:border-[#003B5C]";

const matches = (p, gf) =>
  (gf.department === "all" || p.department === gf.department) &&
  (gf.event === "all" || (gf.event === "__none__" ? !p.event_label : p.event_label === gf.event)) &&
  (gf.status === "all" || (gf.status === "visible" && p.published) || (gf.status === "hidden" && !p.published) || (gf.status === "old" && p.is_old));

const PhotoCard = ({ p, onToggle, onEdit, onDelete }) => (
  <div data-testid={`admin-photo-${p.id}`} className="bg-white border border-gray-100 rounded-sm overflow-hidden">
    <div className="aspect-square bg-[#F7F8FA] overflow-hidden relative">
      <img src={p.url} alt={p.caption || ""} className="w-full h-full object-cover" />
      <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
        {!p.published && <Badge tone="gray" testId={`admin-photo-hidden-badge-${p.id}`}>Hidden</Badge>}
        {p.is_old && <Badge tone="amber" testId={`admin-photo-old-badge-${p.id}`}><Clock size={10} /> Older than 3 years</Badge>}
      </div>
    </div>
    <div className="p-3">
      <div className="text-xs text-[#1E1E1E]/80 truncate mb-1.5" data-testid={`admin-photo-caption-${p.id}`}>{p.caption || <span className="italic text-[#1E1E1E]/45">No caption</span>}</div>
      <div className="flex flex-wrap gap-1 mb-2.5">
        <Badge tone="navy">{p.department || "General Church"}</Badge>
        {p.event_label && <Badge tone="gold">{p.event_label}</Badge>}
      </div>
      <div className="text-[10px] text-[#1E1E1E]/45 mb-2.5 truncate">{p.photo_date ? formatDate(p.photo_date) : `Uploaded ${formatDate((p.created_at || "").slice(0, 10))}`}{p.original_filename ? ` · ${p.original_filename}` : ""}</div>
      <div className="flex items-center gap-1.5">
        <button onClick={() => onToggle(p)} data-testid={`admin-toggle-photo-${p.id}`} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 border border-gray-200 text-xs text-[#003B5C] hover:border-[#003B5C] rounded-sm" title={p.published ? "Hide from public gallery" : "Show in public gallery"}>
          {p.published ? <><Eye size={12} /> Visible</> : <><EyeOff size={12} /> Hidden</>}
        </button>
        <button onClick={() => onEdit(p)} data-testid={`admin-edit-photo-${p.id}`} className="p-1.5 text-[#003B5C] hover:bg-[#003B5C]/10 rounded-sm"><Edit2 size={14} /></button>
        <button onClick={() => onDelete(p)} data-testid={`admin-delete-photo-${p.id}`} className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={14} /></button>
      </div>
    </div>
  </div>
);

export const GalleryPanel = ({ photos, photoLabels, onNew, onEdit, onToggle, onDelete, onFacebook }) => {
  const [gf, setGf] = useState({ department: "all", event: "all", status: "all" });
  const filtered = useMemo(() => photos.filter((p) => matches(p, gf)), [photos, gf]);
  const oldCount = photos.filter((p) => p.is_old).length;
  const setFilter = (k) => (e) => setGf({ ...gf, [k]: e.target.value });

  return (
    <div data-testid="admin-gallery-panel">
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#003B5C]">Gallery Photos</h2>
          <p className="text-sm text-[#1E1E1E]/60 mt-1">Upload JPG/PNG photos, import from a Google Drive link, or connect Facebook. Photos are stored durably and stay visible after redeploys.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onFacebook} data-testid="admin-facebook-import" className="inline-flex items-center gap-2 border border-[#003B5C] text-[#003B5C] px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-[#003B5C]/5"><Facebook size={14} /> Import from Facebook</button>
          <button onClick={onNew} data-testid="admin-new-photo" className="inline-flex items-center gap-2 bg-[#003B5C] text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#002A42]"><Plus size={14} /> New Photo</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <select value={gf.department} onChange={setFilter("department")} data-testid="admin-gallery-filter-department" className={selectCls}>
          <option value="all">All departments</option>{DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={gf.event} onChange={setFilter("event")} data-testid="admin-gallery-filter-event" className={selectCls}>
          <option value="all">All events</option><option value="__none__">No event assigned</option>{photoLabels.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={gf.status} onChange={setFilter("status")} data-testid="admin-gallery-filter-status" className={selectCls}>
          <option value="all">All photos</option><option value="visible">Visible only</option><option value="hidden">Hidden only</option><option value="old">Older than 3 years{oldCount ? ` (${oldCount})` : ""}</option>
        </select>
      </div>

      {gf.status === "old" && (
        <div data-testid="admin-gallery-retention-note" className="mb-6 bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-sm px-4 py-3">
          Retention review: these photos are more than 3 years old (based on photo date, or upload date if no photo date). We recommend <strong>hiding</strong> them first; delete only when you're sure. Nothing is removed automatically.
        </div>
      )}

      {filtered.length === 0 ? (
        <div data-testid="admin-gallery-empty" className="bg-white border border-gray-100 rounded-sm p-12 text-center text-[#1E1E1E]/50">
          {photos.length === 0 ? 'No photos yet. Click "New Photo" to upload your first church image.' : "No photos match these filters."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((p) => <PhotoCard key={p.id} p={p} onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} />)}
        </div>
      )}
    </div>
  );
};
